import { v4 as uuidv4 } from 'uuid';

import {
    updateTabField,
    buildField,
    updateTabFieldArray,
    addFieldsToArray,
    removeFromArray,
    buildCertificateField,
    updateTabFieldArrayValidators,
    getFieldValueIfDirty
} from '../../common/utils';
import { SUBCATEGORIES } from '../../common/categories';
import * as actions from './desktopApplicationActions';
import { commonActionsReducer } from '../../common/actions';
import {
    APPLICATION_SETTINGS_FIELDS,
    DESKTOP_MESSAGING_SERVICES_FIELDS,
    MESSAGING_SERVICE_FIELDS,
    DESKTOP_NOTIFICATION_FIELDS,
    OTP_NOTIFICATION_FIELDS,
    IS_NEW_FIELD
} from '../../common/fields';

const { APPLICATION_SETTINGS, DESKTOP_MESSAGING_SERVICES, DESKTOP_NOTIFICATION, OTP_NOTIFICATION } = SUBCATEGORIES;
const {
    MESSAGING_SERVICES_ENABLED,
    SAAS_MESSAGING_SERVICES_ENABLED,
    MESSAGING_SERVICES_HA_ENABLED,
    DISPLAY_COMPANY_LOGO,
    CUSTOM_MFA_INTEGRATION,
    CUSTOM_MFA_INTEGRATION_KEY,
    USER_ENROLLMENT_WINDOW,
    SECONDARY_URL,
    DEFAULT_CERTIFICATE_REQUEST,
    DEFAULT_CERTIFICATE
} = APPLICATION_SETTINGS_FIELDS;
const { MESSAGING_SERVICES } = DESKTOP_MESSAGING_SERVICES_FIELDS;
const { ID_FIELD, NAME, SERVER_URL, DMZ, CERTIFICATE_REQUEST, CERTIFICATE, CUSTOM_CERTIFICATE } =
    MESSAGING_SERVICE_FIELDS;
const {
    NOTIFICATION_ICON,
    NOTIFICATION_BACKGROUND,
    DISPLAY_ICON_ENABLED,
    NOTIFICATION_HEIGHT,
    NOTIFICATION_WIDTH,
    WINDOW_POSITION,
    NOTIFICATION_TITLE,
    MFA_NOTIFICATION_TEXT,
    TIMEOUT_NOTIFICATION_TEXT,
    NOTIFICATIONS_TIMEOUT,
    SHOW_NOTIFICATIONS_TIMEOUT
} = DESKTOP_NOTIFICATION_FIELDS;
const {
    OTP_NOTIFICATION_ICON,
    OTP_NOTIFICATION_BACKGROUND1,
    OTP_NOTIFICATION_BACKGROUND2,
    OTP_DISPLAY_ICON_ENABLED,
    OTP_NOTIFICATION_HEIGHT,
    OTP_NOTIFICATION_WIDTH,
    OTP_WINDOW_POSITION,
    OTP_NOTIFICATION_TEXT,
    OTP_ON_AUTO_PAIRED_DEVICES
} = OTP_NOTIFICATION_FIELDS;

export const fieldsReducer = (state, action) => {
    switch (action.type) {
        case actions.UPDATE_APPLICATION_SETTINGS:
            return updateTabField(state, APPLICATION_SETTINGS.ID, action.payload.fieldId, action.payload.value);
        case actions.UPDATE_MESSAGING_SERVICE:
            if (action.payload.arrFieldId === CUSTOM_CERTIFICATE.ID) {
                updateTabFieldArrayValidators(
                    state,
                    DESKTOP_MESSAGING_SERVICES.ID,
                    MESSAGING_SERVICES.ID,
                    action.payload.index,
                    CERTIFICATE.ID,
                    action.payload.value ? { dataRequired: true } : {}
                );
            }
            return updateTabFieldArray(
                state,
                DESKTOP_MESSAGING_SERVICES.ID,
                MESSAGING_SERVICES.ID,
                action.payload.index,
                action.payload.arrFieldId,
                action.payload.value
            );
        case actions.ADD_MESSAGING_SERVICE:
            return addFieldsToArray(state, DESKTOP_MESSAGING_SERVICES.ID, MESSAGING_SERVICES.ID, newMessagingService());
        case actions.REMOVE_MESSAGING_SERVICE:
            return removeFromArray(state, DESKTOP_MESSAGING_SERVICES.ID, MESSAGING_SERVICES.ID, action.payload);
        case actions.UPDATE_DESKTOP_NOTIFICATION:
            return updateTabField(state, DESKTOP_NOTIFICATION.ID, action.payload.fieldId, action.payload.value);
        case actions.UPDATE_OTP_NOTIFICATION:
            return updateTabField(state, OTP_NOTIFICATION.ID, action.payload.fieldId, action.payload.value);
        case actions.BACK_TO_DEFAULT:
            return backToDefault(state, action.payload.tabId, action.payload.defaultData);
        default:
            return commonActionsReducer(state, action, initFieldsState);
    }
};

export const initFieldsState = ({ settings }) => {
    return {
        [APPLICATION_SETTINGS.ID]: {
            id: APPLICATION_SETTINGS.ID,
            name: APPLICATION_SETTINGS.NAME,
            fields: {
                [MESSAGING_SERVICES_ENABLED.ID]: buildField(MESSAGING_SERVICES_ENABLED, settings.PushServicesEnabled),
                [SAAS_MESSAGING_SERVICES_ENABLED.ID]: buildField(
                    SAAS_MESSAGING_SERVICES_ENABLED,
                    settings.SaasPushServicesEnabled
                ),
                [MESSAGING_SERVICES_HA_ENABLED.ID]: buildField(MESSAGING_SERVICES_HA_ENABLED, settings.AryaHAMode),
                [DISPLAY_COMPANY_LOGO.ID]: buildField(DISPLAY_COMPANY_LOGO, settings.OverwriteAryaLogo),
                [CUSTOM_MFA_INTEGRATION.ID]: buildField(
                    CUSTOM_MFA_INTEGRATION,
                    settings.PushServicesExternalIntegrationEnabled
                ),
                [CUSTOM_MFA_INTEGRATION_KEY.ID]: buildField(
                    CUSTOM_MFA_INTEGRATION_KEY,
                    settings.PushServicesExternalIntegrationApiKey
                ),
                [USER_ENROLLMENT_WINDOW.ID]: buildField(USER_ENROLLMENT_WINDOW, settings.SimpleTrayOpen),
                [SECONDARY_URL.ID]: buildField(SECONDARY_URL, settings.PushServicesSecondaryUrl, { url: true }),
                [DEFAULT_CERTIFICATE_REQUEST.ID]: buildField(DEFAULT_CERTIFICATE_REQUEST, {
                    data: settings.PushServicesCertificateRequest,
                    fqdn: '',
                    san: ''
                }),
                [DEFAULT_CERTIFICATE.ID]: buildCertificateField(
                    DEFAULT_CERTIFICATE,
                    settings.PushServicesCertificateData
                )
            },
            validators: {}
        },
        [DESKTOP_MESSAGING_SERVICES.ID]: {
            id: DESKTOP_MESSAGING_SERVICES.ID,
            name: DESKTOP_MESSAGING_SERVICES.NAME,
            fields: {
                [MESSAGING_SERVICES.ID]: buildField(
                    MESSAGING_SERVICES,
                    mapMessagingServicesToFields(settings.PushServicesNodesConfigurations)
                )
            },
            validators: {}
        },
        [DESKTOP_NOTIFICATION.ID]: {
            id: DESKTOP_NOTIFICATION.ID,
            name: DESKTOP_NOTIFICATION.NAME,
            fields: mapDesktopNotificationToFields(settings),
            validators: {}
        },
        [OTP_NOTIFICATION.ID]: {
            id: OTP_NOTIFICATION.ID,
            name: OTP_NOTIFICATION.NAME,
            fields: mapOtpNotificationToFields(settings),
            validators: {}
        }
    };
};

export const mapToSettings = (saveAll, selectedTab, fields, oldSettings) => {
    const newSettings = {};
    if (saveAll || selectedTab === APPLICATION_SETTINGS.ID) {
        const applicationFields = fields[APPLICATION_SETTINGS.ID].fields;
        newSettings.PushServicesEnabled = getFieldValueIfDirty(applicationFields[MESSAGING_SERVICES_ENABLED.ID]);
        newSettings.SaasPushServicesEnabled = getFieldValueIfDirty(
            applicationFields[SAAS_MESSAGING_SERVICES_ENABLED.ID]
        );
        if ('AryaHAMode' in oldSettings) {
            newSettings.AryaHAMode = getFieldValueIfDirty(applicationFields[MESSAGING_SERVICES_HA_ENABLED.ID]);
        }
        newSettings.OverwriteAryaLogo = getFieldValueIfDirty(applicationFields[DISPLAY_COMPANY_LOGO.ID]);
        newSettings.PushServicesExternalIntegrationEnabled = getFieldValueIfDirty(
            applicationFields[CUSTOM_MFA_INTEGRATION.ID]
        );
        newSettings.PushServicesExternalIntegrationApiKey = getFieldValueIfDirty(
            applicationFields[CUSTOM_MFA_INTEGRATION_KEY.ID]
        );
        newSettings.SimpleTrayOpen = getFieldValueIfDirty(applicationFields[USER_ENROLLMENT_WINDOW.ID]);
        newSettings.PushServicesSecondaryUrl = getFieldValueIfDirty(applicationFields[SECONDARY_URL.ID]);
        newSettings.PushServicesCertificateRequest = getFieldValueIfDirty(
            applicationFields[DEFAULT_CERTIFICATE_REQUEST.ID],
            applicationFields[DEFAULT_CERTIFICATE_REQUEST.ID].value.data
        );
        newSettings.PushServicesCertificateData = getFieldValueIfDirty(applicationFields[DEFAULT_CERTIFICATE.ID]);
    }
    if (saveAll || selectedTab === DESKTOP_MESSAGING_SERVICES.ID) {
        const messagingFields = fields[DESKTOP_MESSAGING_SERVICES.ID].fields;
        newSettings.PushServicesNodesConfigurations = getFieldValueIfDirty(
            messagingFields[MESSAGING_SERVICES.ID],
            mapFieldsToPushServices(messagingFields[MESSAGING_SERVICES.ID].value, oldSettings)
        );
    }
    if (saveAll || selectedTab === DESKTOP_NOTIFICATION.ID) {
        const desktopNotifyFields = fields[DESKTOP_NOTIFICATION.ID].fields;
        newSettings.NotificationIcon = getFieldValueIfDirty(desktopNotifyFields[NOTIFICATION_ICON.ID]);
        newSettings.NotificationBackground = getFieldValueIfDirty(desktopNotifyFields[NOTIFICATION_BACKGROUND.ID]);
        newSettings.ShowNotificationIcon = getFieldValueIfDirty(desktopNotifyFields[DISPLAY_ICON_ENABLED.ID]);
        newSettings.NotificationHeight = getFieldValueIfDirty(desktopNotifyFields[NOTIFICATION_HEIGHT.ID]);
        newSettings.NotificationWidth = getFieldValueIfDirty(desktopNotifyFields[NOTIFICATION_WIDTH.ID]);
        newSettings.NotificationInCenterPosition = getFieldValueIfDirty(desktopNotifyFields[WINDOW_POSITION.ID]);
        newSettings.CustomNotificationTitle = getFieldValueIfDirty(desktopNotifyFields[NOTIFICATION_TITLE.ID]);
        newSettings.CustomMFANotification = getFieldValueIfDirty(desktopNotifyFields[MFA_NOTIFICATION_TEXT.ID]);
        newSettings.CustomTimeoutNotification = getFieldValueIfDirty(desktopNotifyFields[TIMEOUT_NOTIFICATION_TEXT.ID]);
        newSettings.userMessageTimeout = getFieldValueIfDirty(desktopNotifyFields[NOTIFICATIONS_TIMEOUT.ID]);
        newSettings.hideUserMessageTimeout = getFieldValueIfDirty(
            desktopNotifyFields[SHOW_NOTIFICATIONS_TIMEOUT.ID],
            !desktopNotifyFields[SHOW_NOTIFICATIONS_TIMEOUT.ID].value
        );
    }
    if (saveAll || selectedTab === OTP_NOTIFICATION.ID) {
        const optNotifyFields = fields[OTP_NOTIFICATION.ID].fields;
        newSettings.OtpNotificationIcon = getFieldValueIfDirty(optNotifyFields[OTP_NOTIFICATION_ICON.ID]);
        newSettings.OtpNotificationBackground1 = getFieldValueIfDirty(optNotifyFields[OTP_NOTIFICATION_BACKGROUND1.ID]);
        newSettings.OtpNotificationBackground2 = getFieldValueIfDirty(optNotifyFields[OTP_NOTIFICATION_BACKGROUND2.ID]);
        newSettings.ShowOtpNotificationIcon = getFieldValueIfDirty(optNotifyFields[OTP_DISPLAY_ICON_ENABLED.ID]);
        newSettings.OtpNotificationHeight = getFieldValueIfDirty(optNotifyFields[OTP_NOTIFICATION_HEIGHT.ID]);
        newSettings.OtpNotificationWidth = getFieldValueIfDirty(optNotifyFields[OTP_NOTIFICATION_WIDTH.ID]);
        newSettings.OtpNotificationInCenterPosition = getFieldValueIfDirty(optNotifyFields[OTP_WINDOW_POSITION.ID]);
        newSettings.CustomOtpNotification = getFieldValueIfDirty(optNotifyFields[OTP_NOTIFICATION_TEXT.ID]);
        newSettings.ShowOtpOnAutoPairedDevice = getFieldValueIfDirty(optNotifyFields[OTP_ON_AUTO_PAIRED_DEVICES.ID]);
    }
    return newSettings;
};

export const mapMessagingServicesToFields = (messagingServices) => {
    const messagingServicesFields = [];
    for (const messagingService of messagingServices) {
        messagingServicesFields.push({
            [ID_FIELD.ID]: buildField(ID_FIELD, messagingService.id),
            [NAME.ID]: buildField(NAME, messagingService.name, { required: true }),
            [SERVER_URL.ID]: buildField(SERVER_URL, messagingService.serverUrl, {
                required: true,
                notWhitespace: true,
                httpIpPort: true
            }),
            [DMZ.ID]: buildField(DMZ, messagingService.isDMZ),
            [CUSTOM_CERTIFICATE.ID]: buildField(CUSTOM_CERTIFICATE, messagingService.customCertificate),
            [CERTIFICATE_REQUEST.ID]: buildField(CERTIFICATE_REQUEST, {
                data: messagingService.certificateRequest,
                fqdn: '',
                san: ''
            }),
            [CERTIFICATE.ID]: buildCertificateField(CERTIFICATE, messagingService.certificate),
            [IS_NEW_FIELD.ID]: buildField(IS_NEW_FIELD, messagingService.isNew)
        });
    }
    return messagingServicesFields;
};

const mapDesktopNotificationToFields = (settings) => {
    const { enableWafCompliantValidator } = settings;

    return {
        [NOTIFICATION_ICON.ID]: buildField(NOTIFICATION_ICON, settings.NotificationIcon),
        [NOTIFICATION_BACKGROUND.ID]: buildField(NOTIFICATION_BACKGROUND, settings.NotificationBackground),
        [DISPLAY_ICON_ENABLED.ID]: buildField(DISPLAY_ICON_ENABLED, settings.ShowNotificationIcon),
        [NOTIFICATION_HEIGHT.ID]: buildField(NOTIFICATION_HEIGHT, settings.NotificationHeight, {
            isNumeric: true,
            maxValue: 1000,
            minValue: 100,
            required: true
        }),
        [NOTIFICATION_WIDTH.ID]: buildField(NOTIFICATION_WIDTH, settings.NotificationWidth, {
            isNumeric: true,
            maxValue: 1000,
            minValue: 200,
            required: true
        }),
        [WINDOW_POSITION.ID]: buildField(WINDOW_POSITION, settings.NotificationInCenterPosition),
        [NOTIFICATION_TITLE.ID]: buildField(NOTIFICATION_TITLE, settings.CustomNotificationTitle, {
            required: true,
            maxLength: 100,
            wafCompliantString: enableWafCompliantValidator
        }),
        [MFA_NOTIFICATION_TEXT.ID]: buildField(MFA_NOTIFICATION_TEXT, settings.CustomMFANotification, {
            required: true,
            maxLength: 800,
            wafCompliantString: enableWafCompliantValidator
        }),
        [TIMEOUT_NOTIFICATION_TEXT.ID]: buildField(TIMEOUT_NOTIFICATION_TEXT, settings.CustomTimeoutNotification, {
            required: true,
            maxLength: 800,
            wafCompliantString: enableWafCompliantValidator
        }),
        [NOTIFICATIONS_TIMEOUT.ID]: buildField(NOTIFICATIONS_TIMEOUT, settings.userMessageTimeout, {
            isNumeric: true,
            required: true,
            maxValue: 60,
            minValue: 5
        }),
        [SHOW_NOTIFICATIONS_TIMEOUT.ID]: buildField(SHOW_NOTIFICATIONS_TIMEOUT, !settings.hideUserMessageTimeout)
    };
};

const mapOtpNotificationToFields = (settings) => {
    const { enableWafCompliantValidator } = settings;

    return {
        [OTP_NOTIFICATION_ICON.ID]: buildField(OTP_NOTIFICATION_ICON, settings.OtpNotificationIcon),
        [OTP_NOTIFICATION_BACKGROUND1.ID]: buildField(
            OTP_NOTIFICATION_BACKGROUND1,
            settings.OtpNotificationBackground1
        ),
        [OTP_NOTIFICATION_BACKGROUND2.ID]: buildField(
            OTP_NOTIFICATION_BACKGROUND2,
            settings.OtpNotificationBackground2
        ),
        [OTP_DISPLAY_ICON_ENABLED.ID]: buildField(OTP_DISPLAY_ICON_ENABLED, settings.ShowOtpNotificationIcon),
        [OTP_NOTIFICATION_HEIGHT.ID]: buildField(OTP_NOTIFICATION_HEIGHT, settings.OtpNotificationHeight, {
            isNumeric: true,
            maxValue: 1000,
            minValue: 100,
            required: true
        }),
        [OTP_NOTIFICATION_WIDTH.ID]: buildField(OTP_NOTIFICATION_WIDTH, settings.OtpNotificationWidth, {
            isNumeric: true,
            maxValue: 1000,
            minValue: 200,
            required: true
        }),
        [OTP_WINDOW_POSITION.ID]: buildField(OTP_WINDOW_POSITION, settings.OtpNotificationInCenterPosition),
        [OTP_NOTIFICATION_TEXT.ID]: buildField(OTP_NOTIFICATION_TEXT, settings.CustomOtpNotification, {
            required: true,
            maxLength: 800,
            wafCompliantString: enableWafCompliantValidator
        }),
        [OTP_ON_AUTO_PAIRED_DEVICES.ID]: buildField(OTP_ON_AUTO_PAIRED_DEVICES, settings.ShowOtpOnAutoPairedDevice)
    };
};

export const backToDefault = (fields, tabId, defaultData) => {
    let newFields = {};

    if (tabId === DESKTOP_NOTIFICATION.ID) {
        newFields = mapDesktopNotificationToFields(defaultData);
    } else if (tabId === OTP_NOTIFICATION.ID) {
        newFields = mapOtpNotificationToFields(defaultData);
    } else {
        return fields;
    }

    for (const fieldKey in newFields) {
        newFields[fieldKey].isDirty = true;
    }

    newFields[IS_NEW_FIELD.ID] = buildField(IS_NEW_FIELD, true);
    return {
        ...fields,
        [tabId]: {
            ...fields[tabId],
            fields: newFields
        }
    };
};

export const newMessagingService = () => {
    const newMessagingService = mapMessagingServicesToFields([
        {
            id: uuidv4(),
            name: '',
            serverUrl: '',
            isDMZ: false,
            customCertificate: false,
            certificateRequest: '',
            certificate: '',
            isNew: true
        }
    ]);
    return newMessagingService[0];
};

export const mapFieldsToPushServices = (messagingServicesFields, oldSettings) => {
    return messagingServicesFields.map((ms) => {
        let newMessagingService = {
            id: ms[ID_FIELD.ID].value,
            name: ms[NAME.ID].value,
            serverUrl: ms[SERVER_URL.ID].value,
            isDMZ: ms[DMZ.ID].value,
            customCertificate: ms[CUSTOM_CERTIFICATE.ID].value,
            certificateRequest: ms[CERTIFICATE_REQUEST.ID].value.data,
            certificate: ms[CERTIFICATE.ID].value
        };
        const existing = oldSettings.PushServicesNodesConfigurations.find((s) => s.id === newMessagingService.id);
        if (existing) {
            newMessagingService = { ...existing, ...newMessagingService };
        }
        return newMessagingService;
    });
};
